'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Link from 'next/link';

type ResultType = 'sent' | 'student' | null;

export default function ForgotPasswordClient() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<ResultType>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('請輸入帳號');
      return;
    }
    if (!email.trim()) {
      setError('請輸入 Email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), email: email.trim() }),
      });
      const data = await res.json();
      if (data.type === 'student') {
        setResult('student');
      } else {
        setResult('sent');
      }
    } catch {
      setError('發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  if (result === 'student') {
    return (
      <div className="space-y-4 text-center">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950">
          <h2 className="text-lg font-semibold text-foreground">學生帳號無法自助重設</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            學生（體育股長）帳號的密碼預設為學號。<br />
            如需重設密碼，請聯繫您的學校管理員協助恢復預設值。
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => { setResult(null); setUsername(''); setEmail(''); }}
            className="text-sm text-primary hover:underline"
          >
            重新輸入
          </button>
          <Link href="/login" className="text-sm text-muted-foreground hover:text-primary">
            返回登入
          </Link>
        </div>
      </div>
    );
  }

  if (result === 'sent') {
    return (
      <div className="space-y-4 text-center">
        <div className="rounded-lg border border-border bg-green-50 p-6 dark:bg-green-950">
          <h2 className="text-lg font-semibold text-foreground">已發送重設連結</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            若帳號與 Email 正確，您將會收到一封密碼重設郵件。<br />
            請檢查收件匣（或垃圾郵件夾），連結有效期限為 1 小時。
          </p>
        </div>
        <Link href="/login" className="text-sm text-primary hover:underline">
          返回登入頁面
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="username">帳號</Label>
        <Input
          id="username"
          type="text"
          value={username}
          onChange={e => setUsername(e.target.value)}
          placeholder="請輸入您的登入帳號"
          autoComplete="username"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="請輸入帳號所設定的 Email"
          autoComplete="email"
        />
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? '處理中…' : '送出'}
      </Button>
      <div className="text-center">
        <Link href="/login" className="text-sm text-muted-foreground hover:text-primary">
          返回登入
        </Link>
      </div>
    </form>
  );
}
